import { useRouteContext } from '@/app/layouts/TenantLayout'
import { CreateOrganizationDialog } from '@/app/pages/Organizations/CreateOrganizationDialog'
import { CreateProjectDialog } from '@/app/pages/Projects/CreateProjectDialog'
import { CommandPaletteTrigger } from '@/components/command-palette/CommandPaletteTrigger'
import LogoMark from '@/components/ui/LogoMark'
import { OrgBrandMark } from '@/components/ui/OrgBrandMark'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useSidebar } from '@/components/ui/sidebar-context'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { TruncatedLabel } from '@/components/ui/truncated-label'
import { useCreateOrgNavigation } from '@/hooks/useCreateOrgNavigation'
import { useOrgBrandMark } from '@/hooks/useOrgBrandMark'
import { toOrgSegment, toTenantSegment } from '@/lib/routeScope'
import { cn } from '@/lib/utils'
import {
	findUserOrganization,
	findUserTenant,
	isPlatformUser,
	resolveDefaultTenantForOrg,
	resolveReturnTenantForOrg,
} from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { isConsoleDeployment } from '@/utils/deployment'
import { useIsOwnerOfOrganization } from '@/utils/permissions'
import { useNavigate } from '@tanstack/react-router'
import { ChevronsUpDown } from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { OrganizationPane } from './OrganizationPane'
import { ProjectPane } from './ProjectPane'
import { useProjectSwitch } from './useSwitcherData'

interface OrgProjectSwitcherProps {
	showProjects?: boolean
}

export function OrgProjectSwitcher({ showProjects = true }: OrgProjectSwitcherProps) {
	const navigate = useNavigate()
	const { orgId, tenantId } = useRouteContext()
	const currentOrgId = orgId || null

	const [open, setOpen] = useState(false)
	const [hoveredOrgId, setHoveredOrgId] = useState<string | null>(null)
	const [showCreateProject, setShowCreateProject] = useState(false)
	const flyoutRef = useRef<HTMLDivElement>(null)

	const { isMobile } = useSidebar()
	const platformUser = useAuthStore(s => (s.user && isPlatformUser(s.user) ? s.user : null))
	const storeTenantId = useAuthStore(s => s.currentTenant)
	const orgName = (id: string | null) => (id && findUserOrganization(platformUser, id)?.name) || id || ''
	// The route decides the level: a project id in the URL is the project on screen, even one /me cannot name.
	const isProjectLevel = Boolean(tenantId)
	const projectId =
		tenantId || (currentOrgId ? resolveReturnTenantForOrg(platformUser, currentOrgId, storeTenantId) : null)
	const projectName = projectId ? findUserTenant(platformUser, projectId)?.name || projectId : null
	const switchProject = useProjectSwitch()

	const brandMark = useOrgBrandMark(currentOrgId)
	const isOwner = useIsOwnerOfOrganization(currentOrgId)
	const { showCreateDialog, setShowCreateDialog, handleOpenConsoleOrgCreateModal } =
		useCreateOrgNavigation(currentOrgId)

	const flyoutOrgId = hoveredOrgId ?? currentOrgId

	const close = () => {
		setOpen(false)
		setHoveredOrgId(null)
	}

	const goToOrg = (id: string) => {
		if (isConsoleDeployment()) {
			navigate({ to: '/orgs/$orgId/billing/$type', params: { orgId: toOrgSegment(id), type: 'plan-details' } })
		} else {
			navigate({ to: '/orgs/$orgId/inventory', params: { orgId: toOrgSegment(id) } })
		}
	}

	const handleOrganizationSelect = (id: string) => {
		close()
		// Picking the current org from inside one of its projects still navigates, to leave the project.
		if (id === currentOrgId && !tenantId) {
			return
		}
		toast.info(`Switching to organization "${orgName(id)}"`)
		useAuthStore.getState().setCurrentOrganizationId(id)
		useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(id)

		const project = isConsoleDeployment() ? null : resolveDefaultTenantForOrg(platformUser, id)
		if (project) {
			navigate({
				to: '/orgs/$orgId/projects/$tenantId',
				params: { orgId: toOrgSegment(id), tenantId: toTenantSegment(project) },
			})
		} else {
			goToOrg(id)
		}
	}

	const mark = (
		<OrgBrandMark
			mark={brandMark}
			className='size-7 rounded-md'
			alt='Organization brand mark'
			data-testid='org-switcher-brand-mark'
			fallback={<LogoMark size={20} data-testid='org-switcher-default-brand-mark' />}
		/>
	)
	const organizationLabel = `${orgName(currentOrgId) || 'Organization'} organization`
	const organizationClass = cn(
		'flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors',
		isProjectLevel ? 'bg-sidebar-accent/50' : 'bg-sidebar-accent',
	)
	const switchLabel = showProjects ? 'Switch organization or project' : 'Switch organization'

	return (
		<>
			<Popover open={open} onOpenChange={next => (next ? setOpen(true) : close())}>
				<PopoverAnchor asChild>
					<div className='flex gap-1' data-testid='org-switcher'>
						<Tooltip>
							<TooltipTrigger asChild>
								{isOwner && currentOrgId ? (
									<button
										data-testid='org-switcher-organization'
										type='button'
										aria-label={organizationLabel}
										aria-current={isProjectLevel ? undefined : 'page'}
										className={cn(organizationClass, 'cursor-pointer', isProjectLevel && 'hover:bg-sidebar-accent/75')}
										onClick={() => goToOrg(currentOrgId)}>
										{mark}
									</button>
								) : (
									<span
										data-testid='org-switcher-organization'
										role='img'
										aria-label={organizationLabel}
										className={organizationClass}>
										{mark}
									</span>
								)}
							</TooltipTrigger>
							<TooltipContent side='bottom'>{organizationLabel}</TooltipContent>
						</Tooltip>
						<div className='flex h-9 min-w-0 flex-1 items-center gap-0.5 rounded-lg bg-sidebar-accent/50 p-1'>
							{showProjects ? (
								isProjectLevel ? (
									<button
										data-testid='org-switcher-project'
										type='button'
										aria-current='page'
										className='flex h-7 min-w-0 flex-1 items-center rounded-md bg-sidebar-accent px-2 text-left text-body font-medium text-sidebar-active-text'>
										<TruncatedLabel label={projectName ?? ''} side='bottom' />
									</button>
								) : projectId ? (
									<Tooltip>
										<TooltipTrigger asChild>
											<button
												data-testid='org-switcher-project'
												type='button'
												className='flex h-7 min-w-0 flex-1 cursor-pointer items-center rounded-md px-2 text-left text-body font-medium text-sidebar-inactive-text transition-colors hover:bg-sidebar-accent/50 hover:text-sidebar-active-text'
												onClick={() => switchProject(projectId)}>
												<span className='truncate'>{projectName}</span>
											</button>
										</TooltipTrigger>
										<TooltipContent side='bottom'>Open {projectName}</TooltipContent>
									</Tooltip>
								) : (
									<button
										data-testid='org-switcher-project'
										type='button'
										disabled
										className='flex h-7 min-w-0 flex-1 items-center rounded-md px-2 text-left text-body text-muted-foreground'>
										No projects
									</button>
								)
							) : (
								<TruncatedLabel
									label={orgName(currentOrgId) || 'Select Organization'}
									side='bottom'
									className='min-w-0 flex-1 px-2 text-control font-medium uppercase tracking-wider text-sidebar-active-text'
								/>
							)}
							<Tooltip>
								<TooltipTrigger asChild>
									<PopoverTrigger asChild>
										<button
											data-testid='org-switcher-trigger'
											type='button'
											aria-label={switchLabel}
											className='flex h-7 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent/50 hover:text-foreground aria-expanded:bg-sidebar-accent aria-expanded:text-foreground'>
											<ChevronsUpDown className='size-(--icon-standalone)' aria-hidden />
										</button>
									</PopoverTrigger>
								</TooltipTrigger>
								<TooltipContent side='bottom'>{switchLabel}</TooltipContent>
							</Tooltip>
							<CommandPaletteTrigger />
						</div>
					</div>
				</PopoverAnchor>
				{/* z-100 to clear the bottom bar (z-70); the Popover default of z-50 renders under it. */}
				<PopoverContent
					data-testid='org-switcher-popover'
					className='relative z-100 w-64 p-0 border-border overflow-visible'
					align='start'>
					<OrganizationPane
						currentOrgId={currentOrgId}
						onOrganizationSelect={handleOrganizationSelect}
						onCreateOrgClick={() => {
							close()
							handleOpenConsoleOrgCreateModal()
						}}
						showProjects={showProjects}
						hoveredOrgId={flyoutOrgId}
						onOrganizationHover={setHoveredOrgId}
						onEnterProjects={() => flyoutRef.current?.querySelector('input')?.focus()}
					/>
					{showProjects && flyoutOrgId && (
						<div
							ref={flyoutRef}
							data-testid='project-switcher-flyout'
							className={
								// The mobile sidebar is a sheet with no room beside the menu, so the projects stack under it.
								isMobile
									? 'border-t border-border'
									: 'absolute -top-px left-[calc(100%+1px)] ml-1 w-64 rounded-md border border-border bg-popover text-popover-foreground shadow-levitating'
							}>
							<ProjectPane
								orgId={flyoutOrgId}
								orgName={orgName(flyoutOrgId)}
								onSwitch={close}
								onCreateProjectClick={() => {
									close()
									setShowCreateProject(true)
								}}
							/>
						</div>
					)}
				</PopoverContent>
			</Popover>
			<CreateOrganizationDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} onSuccess={() => {}} />
			<CreateProjectDialog open={showCreateProject} onOpenChange={setShowCreateProject} onSuccess={() => {}} />
		</>
	)
}
